import { useTrackToggle } from '@livekit/components-react'
import { MediaToggleDropdown } from '@workspace/ui/components/media-toggle-dropdown'
import { cn } from '@workspace/ui/lib/utils'
import { type Participant, Track } from 'livekit-client'
import { MicOff, User } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useRoomDevices } from '../hooks/use-room-devices'

function LocalParticipantControls() {
  const { enabled: audioEnabled, toggle: toggleAudio } = useTrackToggle({
    source: Track.Source.Microphone,
  })

  const { enabled: videoEnabled, toggle: toggleVideo } = useTrackToggle({
    source: Track.Source.Camera,
  })

  const {
    audioDevices,
    videoDevices,
    selectedAudioDevice,
    selectedVideoDevice,
    switchAudioDevice,
    switchVideoDevice,
  } = useRoomDevices()

  return (
    <div className="flex gap-1">
      <MediaToggleDropdown
        type="audio"
        enabled={audioEnabled}
        onToggle={() => toggleAudio()}
        devices={audioDevices}
        selectedDeviceId={selectedAudioDevice}
        onDeviceSelect={switchAudioDevice}
        size="sm"
      />
      <MediaToggleDropdown
        type="video"
        enabled={videoEnabled}
        onToggle={() => toggleVideo()}
        devices={videoDevices}
        selectedDeviceId={selectedVideoDevice}
        onDeviceSelect={switchVideoDevice}
        size="sm"
      />
    </div>
  )
}

export function ParticipantTile({ participant }: { participant: Participant }) {
  const isLocal = participant.isLocal
  const isSpeaking = participant.isSpeaking
  const isMuted = participant.isMicrophoneEnabled === false
  const isCameraEnabled = participant.isCameraEnabled

  const videoPublication = participant.getTrackPublication(Track.Source.Camera)
  const videoTrack = videoPublication?.track
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    const videoElement = videoRef.current
    if (!videoElement) return

    if (videoTrack && isCameraEnabled) {
      videoTrack.attach(videoElement)
      return () => {
        videoTrack.detach(videoElement)
      }
    }
  }, [videoTrack, isCameraEnabled])

  const showVideo = videoTrack && isCameraEnabled

  return (
    <div
      className={cn(
        'relative h-full w-full overflow-hidden rounded-lg border-2 bg-card transition-all',
        isSpeaking ? 'border-primary shadow-lg shadow-primary/20' : 'border-border'
      )}
    >
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isLocal}
        className={cn(
          'absolute inset-0 h-full w-full object-cover',
          showVideo ? 'block' : 'hidden'
        )}
        style={{ transform: isLocal ? 'scaleX(-1)' : 'none' }}
      />

      {!showVideo && (
        <div className="absolute inset-0 flex items-center justify-center bg-secondary">
          <div className="flex size-20 items-center justify-center rounded-full bg-primary">
            <User className="size-10 text-primary-foreground" />
          </div>
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-white">
              {participant.name || participant.identity}
              {isLocal && ' (You)'}
            </span>
            {isMuted && !isLocal && <MicOff className="size-4 text-red-400" />}
          </div>
          {isLocal && <LocalParticipantControls />}
        </div>
      </div>
    </div>
  )
}
